import { observer } from 'mobx-react-lite';
import { moreDetails as moreDetails_1 } from './QuickCompose.locstring.json';
import { discard } from 'owa-locstrings/lib/strings/discard.locstring.json';
import { sendButtonLabel } from 'owa-locstrings/lib/strings/sendbuttonlabel.locstring.json';
import { confirmDialogSaveText } from 'owa-locstrings/lib/strings/confirmdialogsavetext.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import ComposeActionButton from 'owa-calendar-forms-common/lib/components/ComposeActionButton';
import getComposeStyles from '../utils/getComposeStyles';
import isQuickComposeInEditMode from 'owa-calendar-compose-form-store/lib/selectors/isQuickComposeInEditMode';
import QuickComposeHeader from './QuickComposeHeader';
import { useGlobalHotkey } from 'owa-hotkeys';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { getCommands } from 'owa-calendar-hotkeys';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import { lazyIsQuickComposeOpen } from 'owa-calendar-compose-form-store';
import { Link } from '@fluentui/react/lib/Link';
import {
    FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
    QuickComposeToFullComposeEntryType,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    logQuickComposeToFullComposeEntryType,
    discardQuickComposeEvent,
    verifyEventThenSave,
    saveAndSendDraft,
    switchToFullCompose,
} from 'owa-calendar-compose-form-actions';
import DraftSplitButton from 'owa-calendar-forms-common/lib/components/DraftSplitButton';
import { isCssVariablesFeatureEnabled } from 'owa-calendar-feature-flags';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import QuickComposeContent from './QuickComposeContent';
import EventContent from './EventContent';
import 'owa-calendar-compose-form-orchestration';
import { isConsumer } from 'owa-session-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';
import { getComposeEvent, getQuickComposeViewState } from 'owa-calendar-compose-store-manager';
import { CalendarFormPerfHelper } from 'owa-calendar-forms-perf';

import styles from './QuickCompose.scss';
import classNames from 'classnames';

/**
 * Props for quick compose
 */
export interface QuickComposeProps {
    /**
     * Id for this compose form
     */
    formId: string;

    /**
     * Callback for when a key is pressed on quick compose
     */
    onKeyDown?: React.KeyboardEventHandler<EventTarget>;

    /**
     * Callback when for when the component's height increases to rerender the container
     * to ensure it does not go off screen.
     * Pass null if you think your scenario does not need to resize the component height
     */
    onSizeUpdate?: () => void;
}

export default observer(function QuickCompose(props: QuickComposeProps) {
    if (
        !lazyIsQuickComposeOpen.tryImportForRender()?.(props.formId) ||
        !getComposeEvent(props.formId)
    ) {
        return null;
    }

    return <QuickComposeForm {...props} />;
});

/**
 * Component for rendering quick compose
 */
const QuickComposeForm = observer(function QuickComposeForm(props: QuickComposeProps) {
    const { formId, onKeyDown, onSizeUpdate } = props;

    const viewState = getQuickComposeViewState(formId);
    const event = getComposeEvent(formId);

    const container = React.useRef<HTMLDivElement>();
    const lastHeight = React.useRef<number>();

    React.useEffect(() => {
        if (container.current) {
            lastHeight.current = container.current.getBoundingClientRect().height;
        }
    }, []);

    const onSaveAndSend = React.useCallback(() => {
        verifyEventThenSave(formId);
    }, [formId]);

    const onSaveDraft = React.useCallback(() => {
        saveAndSendDraft(formId);
    }, [formId]);

    useGlobalHotkey(getCommands().saveAppointment, onSaveAndSend, {
        allowHotkeyOnTextFields: true,
        preventDefault: true,
        isEnabled: () => {
            return !isEventAMeeting(event);
        },
    });
    useGlobalHotkey(getCommands().sendMeeting, onSaveAndSend, {
        allowHotkeyOnTextFields: true,
        preventDefault: true,
        isEnabled: () => {
            return isEventAMeeting(event);
        },
    });

    const updateContainerSize = React.useCallback(() => {
        if (!onSizeUpdate) {
            // if onSizeUpdate is not passed, then do nothing.
            return;
        }

        if (container.current) {
            const currentHeight = container.current.getBoundingClientRect().height;
            if (currentHeight > lastHeight.current) {
                onSizeUpdate();
            }
            lastHeight.current = currentHeight;
        }
    }, [onSizeUpdate]);

    React.useEffect(updateContainerSize);

    const onMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // prevent mouseDown event from propagating outside the peek content so it's not interpreted as a drag
        e.stopPropagation();
    }, []);

    const onLaunchFullCompose = React.useCallback(
        (
            shouldOpenSchedulingAssistant?: boolean,
            createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps
        ) => {
            switchToFullCompose(
                formId,
                FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
                shouldOpenSchedulingAssistant,
                createCollabSpaceOnInitProps
            );
        },
        [formId]
    );

    const discardButtonClicked = React.useCallback(() => {
        discardQuickComposeEvent(formId);
    }, [formId]);

    const isInEditMode = isQuickComposeInEditMode(formId);
    const colorScheme: ColorScheme = getComposeCalendarColorScheme(formId);
    // This function is to support cal-css-variables feature flag and generate the expected style
    const composeGeneratedStyles = getComposeStyles(
        !isCssVariablesFeatureEnabled(),
        colorScheme,
        styles.useCssVariables,
        styles.editTitleTextInputCssVariables
    );
    const isMeeting = isEventAMeeting(event);
    const showDraftSplitButton = isFeatureEnabled('cal-saveDraftEvents');
    const commitButtonLabel: string = isMeeting ? loc(sendButtonLabel) : loc(confirmDialogSaveText);
    const viewType = viewState.viewType;
    const isTimePanelView = viewType == 'TimePanel';
    const showMergedEvent: boolean = !isConsumer() && !isInEditMode;

    return (
        // tabIndex = -1 ensures that the form takes focus on click (even on blank area) but doesn't impact the tab order
        <div
            style={composeGeneratedStyles.quickComposeStyle}
            ref={container}
            className={isInEditMode ? styles.quickEdit : styles.quickCompose}
            onKeyDown={onKeyDown}
            tabIndex={-1}>
            <CalendarFormPerfHelper formId={formId} />
            {!isTimePanelView && (
                <QuickComposeHeader
                    className={
                        isFeatureEnabled('cal-surface-dragPeek')
                            ? styles.calendarItemBar
                            : undefined
                    }
                    onLaunchFullComposeCallback={onMoreOptionsButtonClicked(
                        formId,
                        QuickComposeToFullComposeEntryType.IconButton,
                        onLaunchFullCompose
                    )}
                    colorScheme={colorScheme}
                />
            )}
            <div className={styles.content} onMouseDown={onMouseDown}>
                {showMergedEvent ? (
                    <EventContent
                        formId={formId}
                        event={event}
                        colorScheme={colorScheme}
                        onLaunchFullComposeCallback={onLaunchFullCompose}
                    />
                ) : (
                    <QuickComposeContent
                        formId={formId}
                        event={event}
                        colorScheme={colorScheme}
                        onSizeUpdate={updateContainerSize}
                        onLaunchFullComposeCallback={onLaunchFullCompose}
                    />
                )}

                <div className={styles.footer} style={composeGeneratedStyles.footerStyle}>
                    {isMeeting && showDraftSplitButton ? (
                        <DraftSplitButton
                            onSend={onSaveAndSend}
                            caption={commitButtonLabel}
                            onSaveAsDraft={onSaveDraft}
                            colors={colorScheme}
                        />
                    ) : (
                        <ComposeActionButton
                            colors={composeGeneratedStyles.composeActionButtonColors}
                            id={commitButtonLabel}
                            className={classNames(
                                styles.saveButton,
                                composeGeneratedStyles.cssVariabelsStyle
                            )}
                            caption={commitButtonLabel}
                            onClick={onSaveAndSend}
                        />
                    )}
                    <DefaultButton
                        styles={composeGeneratedStyles.discardButtonStyle}
                        className={classNames(
                            styles.discardButton,
                            composeGeneratedStyles.cssVariabelsStyle
                        )}
                        text={loc(discard)}
                        onClick={discardButtonClicked}
                    />
                    {!isTimePanelView && (
                        <Link
                            styles={composeGeneratedStyles.linkGetStyles}
                            onClick={onMoreOptionsButtonClicked(
                                formId,
                                QuickComposeToFullComposeEntryType.MoreOptionsButton,
                                onLaunchFullCompose
                            )}
                            className={classNames(
                                styles.moreDetails,
                                composeGeneratedStyles.cssVariabelsStyle
                            )}>
                            {loc(moreDetails_1)}
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
});

function onMoreOptionsButtonClicked(
    formId: string,
    entryType: QuickComposeToFullComposeEntryType,
    onLaunchFullCompose: (
        shouldOpenSchedulingAssistant?: boolean,
        createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps
    ) => void
) {
    return () => {
        logQuickComposeToFullComposeEntryType(formId, entryType);
        onLaunchFullCompose();
    };
}
